import { v4 as uuidv4 } from "uuid";
import {
  CreateDomain,
  CreateDomainPort,
  CreateDomainPortRoute,
  CreateDomainSpec,
} from "../../../../schema/types/domain/create";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import {
  defaultCiphers,
  Domain,
  DomainPort,
  DomainPortRoute,
  DomainPortTls,
} from "../../../../schema/types/domain/entity";
import {
  DomainInput,
  DomainInputRoutingMode,
  DomainPortCorsAllowOriginInput,
  DomainPortCorsInput,
  DomainPortInput,
  DomainPortRouteInput,
  DomainPortRouteMirrorInput,
  DomainPortTlsInput,
} from "../../../../schema/types/domain/input";
import { NameValuePair, OptimisticConcurrency } from "../../../../schema/types/base";
import { inputToTags, tagsToInput } from "../../../../mst/kinds/utils";
import { linksOf } from "../../../../services/cpln";
import { getDefaultKindSelectState } from "../../../../components/antd/kindselect/KindSelect";
import { isInternalDomain } from "../../../../services/utils";

export function getDefaultDomainPortCors(): DomainPortCorsInput {
  return {
    isEnabled: false,
    allowCredentials: false,
    allowHeaders: [],
    allowMethods: [],
    allowOrigins: [],
    exposeHeaders: [],
    maxAge: "",
  };
}

// TODO check the value property of kind select state for workload if we show relative to gvc

export function getDefaultDomainPortTls(): DomainPortTlsInput {
  return {
    isEnabled: false,
    cipherSuites: defaultCiphers,
    clientCertificate: {
      isEnabled: false,
      secret: getDefaultKindSelectState(),
    },
    serverCertificate: {
      isEnabled: false,
      secret: getDefaultKindSelectState(),
    },
    minProtocolVersion: "TLSV1_2",
  };
}

export function getDefaultDomainPortRouteMirrorInput(): DomainPortRouteMirrorInput {
  return {
    id: uuidv4(),
    workload: getDefaultKindSelectState(),
    percent: "",
    port: "",
  };
}

export function getDefaultDomainPortRouteInput(): DomainPortRouteInput {
  return {
    id: uuidv4(),
    headers: [],
    hostType: "prefix",
    prefixType: "prefix",
    hostPrefix: "",
    hostRegex: "",
    port: "",
    prefix: "",
    regex: "",
    replacePrefix: "",
    replica: "",
    workload: getDefaultKindSelectState(),
    mirror: [],
  };
}

export function getDefaultDomainCorsAllowOriginInput(): DomainPortCorsAllowOriginInput {
  return {
    id: uuidv4(),
    type: "exact",
    value: "",
  };
}

export function getDefaultDomainPortInput(): DomainPortInput {
  return {
    id: uuidv4(),
    number: "443",
    protocol: "http2",
    routes: [],
    tls: getDefaultDomainPortTls(),
    cors: getDefaultDomainPortCors(),
  };
}

export function getDefaultDomainInput(): DomainInput {
  return {
    id: "",
    version: 0,
    tags: [],
    gvc: getDefaultKindSelectState(),
    basic: {
      name: "",
      description: "",
      workload: getDefaultKindSelectState(),
    },
    advanced: {
      name: "",
      description: "",
      spec: {
        acceptAll: "none",
        certChallengeType: "http01",
        dnsMode: "cname",
        workload: getDefaultKindSelectState(),
        ports: [],
        routingMode: "pathBased",
      },
    },
    meta: {
      mode: "basic",
      draftType: "create",
      portActiveKey: "",
      domainLink: "",
      previewing: false,
      debugging: false,
      dryRun: {
        message: "",
        apex: null,
        ownership: {
          checkedName: "",
          passed: false,
          config: null,
        },
        dns: null,
      },
    },
  };
}

export function domainBasicToAdvanced(currentValues: DomainInput) {
  const basicInput: DomainInput = JSON.parse(JSON.stringify(currentValues));

  const portId = uuidv4();

  const defaultInput = getDefaultDomainInput();

  const advancedInput: DomainInput = {
    ...defaultInput,
    ...basicInput,
    advanced: {
      name: basicInput.basic?.name || "",
      description: basicInput.basic.description || "",
      spec: {
        dnsMode: "cname",
        certChallengeType: "http01",
        acceptAll: "none",
        workload: getDefaultKindSelectState(),
        routingMode: "pathBased",
        ports: [
          {
            ...getDefaultDomainPortInput(),
            id: portId,
            number: "443",
            protocol: "http2",
            routes: basicInput.basic.workload.value
              ? [
                  {
                    ...getDefaultDomainPortRouteInput(),
                    prefix: `/`,
                    workload: basicInput.basic.workload,
                  },
                ]
              : [],
          },
        ],
      },
    },
    meta: {
      ...defaultInput.meta,
      mode: "advanced",
      portActiveKey: portId,
    },
  };

  if (basicInput.basic.workload.link) {
    const { org, gvc } = parseLink(basicInput.basic.workload.link, { useInputCtx: true });
    const gvcLink = `/org/${org}/gvc/${gvc}`;
    if (advancedInput.gvc.link !== gvcLink) {
      advancedInput.gvc = {
        link: gvcLink,
        value: gvc,
      };
    }
  }

  return advancedInput;
}

function inputToPort(p: DomainPortInput, d: DomainInput): CreateDomainPort {
  const port: CreateDomainPort = {
    protocol: p.protocol,
    routes: [],
  };

  if (p.number.length > 0) {
    port.number = Number(p.number);
  }

  for (const r of p.routes) {
    const route = inputToRoute(r, d);
    port.routes?.push(route);
  }

  const mustEnableTLS = (!p.number || p.number === "443") && p.protocol !== "tcp";
  if (p.tls.isEnabled || mustEnableTLS) {
    port.tls = {
      minProtocolVersion: p.tls.minProtocolVersion,
      cipherSuites: p.tls.cipherSuites as any[], // can maybe filter out empty items on lists?
    };
    if (p.tls.clientCertificate.isEnabled) {
      port.tls.clientCertificate = {};
      if (p.tls.clientCertificate.secret.value) {
        port.tls.clientCertificate.secretLink = p.tls.clientCertificate.secret.link;
      }
    }
    if (p.tls.serverCertificate.isEnabled) {
      port.tls.serverCertificate = {};
      if (p.tls.serverCertificate.secret.value) {
        port.tls.serverCertificate.secretLink = p.tls.serverCertificate.secret.link;
      }
    }
  }

  if (p.cors.isEnabled) {
    port.cors = {
      allowOrigins: p.cors.allowOrigins.map((ao) => {
        if (ao.type === "regex") {
          return { regex: ao.value };
        }
        return { exact: ao.value };
      }),
      allowCredentials: p.cors.allowCredentials,
      allowHeaders: p.cors.allowHeaders.map((i) => i.value),
      exposeHeaders: p.cors.exposeHeaders.map((i) => i.value),
      allowMethods: p.cors.allowMethods.map((i) => i.value),
    };
    if (p.cors.maxAge.length > 0) {
      port.cors.maxAge = p.cors.maxAge;
    }
  }
  return port;
}

function inputToRoute(r: DomainPortRouteInput, d: DomainInput): CreateDomainPortRoute {
  let workloadLink = "";
  const s = d.advanced.spec;
  if (s.routingMode === "workloadSubdomain") {
    workloadLink = s.workload.link!;
  } else {
    if (r.workload.value) {
      const gvcName = parseLink(d.gvc.link, { useInputCtx: true }).name;
      const workloadName = parseLink(r.workload.link, { useInputCtx: true }).name;
      workloadLink = `//gvc/${gvcName}/workload/${workloadName}`;
    }
  }

  const route: CreateDomainPortRoute = {
    workloadLink: workloadLink,
  };

  if (r.prefixType === "regex" && r.regex.length > 0) {
    route.regex = r.regex;
  } else if (r.prefixType === "prefix" && r.prefix.length > 0) {
    route.prefix = r.prefix;
  }

  if (s.acceptAll !== "none") {
    if (r.hostType === "regex" && r.hostRegex.length > 0) {
      route.hostRegex = r.hostRegex;
    } else if (r.hostType === "prefix" && r.hostPrefix.length > 0) {
      route.hostPrefix = r.hostPrefix;
    }
  }

  if (r.port.length > 0) {
    route.port = Number(r.port);
  }
  if (r.replica.length > 0) {
    route.replica = Number(r.replica);
  }
  if (r.replacePrefix.length > 0) {
    route.replacePrefix = r.replacePrefix;
  }

  const headers = {
    request: {
      set: {},
    },
  };
  const set = {};
  for (const header of r.headers) {
    if (!header.name) {
      continue;
    }
    set[header.name] = header.value;
  }
  headers.request.set = set;

  if (Object.keys(set).length > 0) {
    route.headers = headers;
  }

  if (r.mirror && r.mirror.length > 0) {
    const mirrorItems: { workloadLink: string; percent: number; port?: number }[] = [];
    for (const m of r.mirror) {
      if (m.workload.value && m.percent.length > 0) {
        const parsed = parseLink(m.workload.link, { useInputCtx: true });
        const item: { workloadLink: string; percent: number; port?: number } = {
          workloadLink: `//gvc/${parsed.gvc}/workload/${parsed.name}`,
          percent: Number(m.percent),
        };
        if (m.port && m.port.length > 0) {
          item.port = Number(m.port);
        }
        mirrorItems.push(item);
      }
    }
    if (mirrorItems.length > 0) {
      route.mirror = mirrorItems;
    }
  }

  return route;
}

function routeToInput(r: DomainPortRoute): DomainPortRouteInput {
  const { name: workloadName } = parseLink(r.workloadLink || "", { useInputCtx: true });

  const route: DomainPortRouteInput = {
    ...getDefaultDomainPortRouteInput(),
    workload: {
      value: workloadName,
      link: r.workloadLink || "",
    },
  };

  if (r.regex && r.regex.length > 0) {
    route.prefixType = "regex";
    route.regex = r.regex;
  } else if (r.prefix && r.prefix.length > 0) {
    route.prefixType = "prefix";
    route.prefix = r.prefix;
  }

  if (r.hostRegex && r.hostRegex.length > 0) {
    route.hostType = "regex";
    route.hostRegex = r.hostRegex;
  } else if (r.hostPrefix && r.hostPrefix.length > 0) {
    route.hostType = "prefix";
    route.hostPrefix = r.hostPrefix;
  }

  if (r.port !== undefined) {
    route.port = String(r.port);
  }
  if (r.replica !== undefined) {
    route.replica = String(r.replica);
  }
  if (r.replacePrefix && r.replacePrefix.length > 0) {
    route.replacePrefix = r.replacePrefix;
  }

  const headers: NameValuePair[] = [];
  const headerSet = r.headers?.request?.set || {};
  for (const [name, value] of Object.entries(headerSet)) {
    headers.push({
      id: uuidv4(),
      name,
      value: String(value),
    });
  }
  route.headers = headers;

  const mirrorItems: DomainPortRouteMirrorInput[] = [];
  for (const m of r.mirror || []) {
    const parsed = parseLink(m.workloadLink || "", { useInputCtx: true });
    mirrorItems.push({
      id: uuidv4(),
      workload: {
        value: parsed.gvcRelative || parsed.name,
        link: m.workloadLink || "",
      },
      percent: String(m.percent),
      port: m.port !== undefined ? String(m.port) : "",
    });
  }
  route.mirror = mirrorItems;

  return route;
}

function portToInput(p: DomainPort): DomainPortInput {
  const port: DomainPortInput = {
    ...getDefaultDomainPortInput(),
    number: String(p.number),
    // TODO for example for this, there is actually two domain types on the backend, one if for create with optional, other is after the creation which doesnt have optionals
    protocol: p.protocol!,
    routes: [],
  };

  for (const route of p.routes || []) {
    const routeInput = routeToInput(route);
    port.routes.push(routeInput);
  }

  if (p.tls) {
    port.tls = {
      isEnabled: true,
      minProtocolVersion: p.tls.minProtocolVersion || "TLSV1_2",
      cipherSuites: p.tls.cipherSuites || defaultCiphers,
      clientCertificate: {
        isEnabled: !!p.tls.clientCertificate,
        secret: {
          value: "",
          link: p.tls.clientCertificate?.secretLink || "",
        },
      },
      serverCertificate: {
        isEnabled: !!p.tls.serverCertificate,
        secret: {
          value: "",
          link: p.tls.serverCertificate?.secretLink || "",
        },
      },
    };

    // Extract secret names from links
    if (p.tls.clientCertificate?.secretLink) {
      const { name } = parseLink(p.tls.clientCertificate.secretLink, { useInputCtx: true });
      port.tls.clientCertificate.secret.value = name;
    }
    if (p.tls.serverCertificate?.secretLink) {
      const { name } = parseLink(p.tls.serverCertificate.secretLink, { useInputCtx: true });
      port.tls.serverCertificate.secret.value = name;
    }
  }

  // Convert CORS
  if (p.cors) {
    port.cors = {
      isEnabled: true,
      allowCredentials: p.cors.allowCredentials || false,
      allowHeaders: (p.cors.allowHeaders || []).map((i) => ({ id: uuidv4(), value: i })),
      allowMethods: (p.cors.allowMethods || []).map((i) => ({ id: uuidv4(), value: i })),
      exposeHeaders: (p.cors.exposeHeaders || []).map((i) => ({ id: uuidv4(), value: i })),
      maxAge: p.cors.maxAge || "",
      allowOrigins: [],
    };

    // Convert allowOrigins
    for (const allowOrigin of p.cors.allowOrigins || []) {
      port.cors.allowOrigins.push({
        id: uuidv4(),
        type: allowOrigin.regex ? "regex" : "exact",
        value: allowOrigin.regex || allowOrigin.exact || "",
      });
    }
  }

  return port;
}

export function inputToDomain(_input: DomainInput): CreateDomain {
  const input: DomainInput = JSON.parse(JSON.stringify(_input));
  if (input.meta.mode === "basic") {
    const b = input.basic;

    const body: CreateDomain = {
      name: b.name,
      description: b.description,
      tags: inputToTags(input.tags),
      kind: "domain",
      spec: {
        dnsMode: "cname",
        certChallengeType: "http01",
        ports: [
          {
            number: 443,
            protocol: "http2",
            routes: [
              {
                workloadLink: b.workload.link,
                prefix: "/",
              },
            ],
          },
        ],
      },
    };
    return body;
  }

  const a = input.advanced;

  const domain: CreateDomain = {
    name: a.name,
    description: a.description,
    kind: "domain",
    tags: inputToTags(input.tags),
    spec: {},
  };

  const s = a.spec;

  const spec: CreateDomainSpec = {
    acceptAllHosts: s.acceptAll === "hosts",
    acceptAllSubdomains: s.acceptAll === "subdomains",
    dnsMode: s.dnsMode,
  };
  if (s.certChallengeType && !isInternalDomain(a.name)) {
    spec.certChallengeType = s.certChallengeType;
  }
  if (s.routingMode === "gvcSubdomain" && !isInternalDomain(a.name)) {
    spec.gvcLink = input.gvc.link;
  }
  if (s.routingMode === "workloadSubdomain") {
    spec.workloadLink = s.workload.link;
  }

  if (s.routingMode !== "none") {
    const ports: CreateDomainPort[] = [];
    for (const p of s.ports) {
      const port = inputToPort(p, input);
      if (s.routingMode === "gvcSubdomain") {
        port.routes = [];
      }
      ports.push(port);
    }
    spec.ports = ports;
  }
  domain.spec = spec;
  return domain;
}

export function domainToInput(domain?: Domain): DomainInput {
  if (!domain) {
    return getDefaultDomainInput();
  }

  let isAdvanced = false;
  if (domain.spec?.dnsMode !== "cname") {
    isAdvanced = true;
  }
  if (domain.spec?.certChallengeType !== "http01") {
    isAdvanced = true;
  }
  if (!!domain.spec?.gvcLink) {
    isAdvanced = true;
  }
  if (!!domain.spec?.workloadLink) {
    isAdvanced = true;
  }
  if (!!domain.spec?.acceptAllHosts) {
    isAdvanced = true;
  }
  if (!!domain.spec?.acceptAllSubdomains) {
    isAdvanced = true;
  }
  if (domain.spec?.ports?.length !== 1) {
    isAdvanced = true;
  }
  for (const port of domain.spec?.ports || []) {
    if (port.number !== 443) {
      isAdvanced = true;
    }
    if (port.protocol !== `http2`) {
      isAdvanced = true;
    }
    if (port.routes?.length !== 1) {
      isAdvanced = true;
    }
    for (const route of port.routes || []) {
      if (Object.keys(route.headers?.request?.set || {}).length > 0) {
        isAdvanced = true;
      }
      if (!!route.hostPrefix) {
        isAdvanced = true;
      }
      if (!!route.hostRegex) {
        isAdvanced = true;
      }
      if (!!route.port) {
        isAdvanced = true;
      }
      if (route.prefix !== `/`) {
        isAdvanced = true;
      }
      if (!!route.regex) {
        isAdvanced = true;
      }
      if (!!route.replacePrefix) {
        isAdvanced = true;
      }
      if (route.replica !== undefined) {
        isAdvanced = true;
      }
      if (route.mirror && route.mirror.length > 0) {
        isAdvanced = true;
      }
      if (!route.workloadLink) {
        isAdvanced = true;
      }
    }

    if (!isTlsDefault(port.tls)) {
      isAdvanced = true;
    }

    if (!!port.cors) {
      isAdvanced = true;
    }
  }

  const selfLink = linksOf(domain).self!;

  if (!isAdvanced) {
    const { absolute, gvcRelative } = parseLink(domain.spec?.ports![0].routes![0].workloadLink!, {
      useInputCtx: true,
    });
    const defaultInput = getDefaultDomainInput();
    const form: DomainInput = {
      ...defaultInput,
      id: domain.id,
      version: domain.version,
      tags: tagsToInput(domain.tags),
      basic: {
        name: domain.name,
        description: domain.description,
        workload: {
          value: gvcRelative,
          link: absolute,
        },
      },
      meta: {
        ...defaultInput.meta,
        mode: "basic",
        draftType: "edit",
        domainLink: selfLink,
      },
    };
    return form;
  }

  let routingMode: DomainInputRoutingMode = "none";
  if (domain.spec?.gvcLink) {
    routingMode = "gvcSubdomain";
  } else if (domain.spec?.workloadLink) {
    routingMode = "workloadSubdomain";
  } else if ((domain.spec?.ports || []).some((p) => (p.routes || []).length > 0)) {
    // TODO we can check here more things
    routingMode = "pathBased";
  }

  const defaultInput = getDefaultDomainInput();
  const form: DomainInput = {
    ...defaultInput,
    id: domain.id,
    version: domain.version,
    tags: tagsToInput(domain.tags),
    advanced: {
      name: domain.name,
      description: domain.description,
      spec: {
        acceptAll: domain.spec?.acceptAllHosts ? "hosts" : domain.spec?.acceptAllSubdomains ? "subdomains" : "none",
        certChallengeType: domain.spec?.certChallengeType || "",
        dnsMode: domain.spec?.dnsMode || "cname",
        workload: {
          value: parseLink(domain.spec?.workloadLink || "", { useInputCtx: true }).gvcRelative,
          link: domain.spec?.workloadLink || "",
        },
        routingMode: routingMode,
        ports: (domain.spec?.ports || []).map((p) => portToInput(p)),
      },
    },
    meta: {
      ...defaultInput.meta,
      mode: "advanced",
      draftType: "edit",
      domainLink: selfLink,
    },
  };

  if (routingMode === "pathBased") {
    let done = false;
    for (const port of domain.spec?.ports || []) {
      for (const route of port.routes || []) {
        if (route.workloadLink) {
          done = true;
          const { org, gvc } = parseLink(route.workloadLink, { useInputCtx: true });
          form.gvc = {
            value: gvc,
            link: `/org/${org}/gvc/${gvc}`,
          };
          break;
        }
      }
      if (done) {
        break;
      }
    }
  } else if (routingMode === "gvcSubdomain") {
    const { gvc } = parseLink(domain.spec?.gvcLink!, { useInputCtx: true });
    form.gvc = {
      value: gvc,
      link: domain.spec?.gvcLink || "",
    };
  } else if (routingMode === "workloadSubdomain") {
    const { org, gvc } = parseLink(domain.spec?.workloadLink!, { useInputCtx: true });
    form.gvc = {
      value: gvc,
      link: `/org/${org}/gvc/${gvc}`,
    };
  }

  return form;
}

function isTlsDefault(tls: DomainPortTls | undefined): boolean {
  if (tls) {
    if (tls.cipherSuites?.length !== 8) {
      return false;
    }
    for (const cipherSuite of tls.cipherSuites || []) {
      if (
        ![
          "ECDHE-ECDSA-AES256-GCM-SHA384",
          "ECDHE-ECDSA-CHACHA20-POLY1305",
          "ECDHE-ECDSA-AES128-GCM-SHA256",
          "ECDHE-RSA-AES256-GCM-SHA384",
          "ECDHE-RSA-CHACHA20-POLY1305",
          "ECDHE-RSA-AES128-GCM-SHA256",
          "AES256-GCM-SHA384",
          "AES128-GCM-SHA256",
        ].includes(cipherSuite)
      ) {
        return false;
      }
    }
    if (tls.minProtocolVersion !== "TLSV1_2") {
      return false;
    }
    if (tls.clientCertificate) {
      return false;
    }
    if (tls.serverCertificate) {
      return false;
    }
  }
  return true;
}

export function domainToPatch(_domain: CreateDomain, concurrency: OptimisticConcurrency) {
  const domain: any = JSON.parse(JSON.stringify(_domain));

  domain[`$replace/spec`] = domain.spec;
  delete domain.spec;

  domain[`$replace/tags`] = domain.tags;
  delete domain.tags;

  domain.id = concurrency.id;
  domain.version = concurrency.version;

  return domain;
}
